<script>
import _ from 'lodash'

import LabReportItem from './LabReportItem'
import LabReportErrorItem from './LabReportErrorItem'
import { BIOMARKERS } from '../../graphql/queries/BiomarkerQueries'
import { normalizeLabReport } from '../../utils/labReport'
import { ContentLoader } from 'vue-content-loader'
import { isMobile } from 'mobile-device-detect'

export default {
  name: 'LabReportForm',
  components: {
    ContentLoader,
    LabReportItem,
    LabReportErrorItem
  },
  props: {
    labReport: {
      type: Object,
      required: false,
      default: () => ({
        date: new Date(),
        lab: '',
        results: [],
        errorResults: []
      })
    }
  },
  data() {
    return {
      loading: false,
      searchedBiomarker: '',
      isMobile: isMobile,
      file: null,
      errorRows: []
    }
  },
  apollo: {
    // ToDo: optimize
    biomarkers: BIOMARKERS
  },
  computed: {
    getBiomarkersList() {
      if (!this.biomarkers) return []
      const addedBiomarkers = this.labReport.results.map(x => x.id)
      if (this.labReport.errorResults)
        addedBiomarkers.concat(this.labReport.errorResults.map(x => x.id))
      return this.biomarkers.filter(x => !_.includes(addedBiomarkers, x.id))
    },
    getSearchedBiomarkers() {
      const search = this.searchedBiomarker.toLowerCase()

      const filterByNameAndSynonyms = ({ name, synonyms }) =>
        _.some([name, ...synonyms], x => _.includes(x.toLowerCase(), search))

      const sortByNameDesc = (a, b) => (a.name > b.name ? 1 : -1)

      return this.getBiomarkersList
        .filter(filterByNameAndSynonyms)
        .sort(sortByNameDesc)
    }
  },
  methods: {
    selectBiomarker(option) {
      if (option) {
        const result = !option.units
          ? option
          : { ...option, unit: _.first(option.units) }
        this.labReport.results.push(result)
        this.clearSearch()
      }
    },
    findIndex(item) {
      return _.findIndex(this.labReport.results, x => x.id === item.id)
    },
    updateBiomarker(item, option) {
      const index = this.findIndex(item)
      if (option) this.labReport.results.splice(index, 1, option)
      else this.labReport.results.splice(index, 1)
    },
    updateErrorBiomarker(item, option) {
      const index = _.findIndex(this.labReport.errorResults, x =>
        item.id ? x.id === item.id : x.index === item.index
      )
      if (option) this.labReport.errorResults.splice(index, 1, option)
      else {
        this.labReport.errorResults.splice(index, 1)
        this.errorRows.splice(index, 1)
      }
      this.$forceUpdate()
    },
    clearSearch() {
      this.$nextTick(function() {
        this.searchedBiomarker = ''
      })
    },
    submit() {
      this.$emit('submit', normalizeLabReport(this.labReport))
    },
    getInputType(item) {
      const { type } = item.range
      const isNumber = type === 'universal' || type === 'gender'

      return isNumber ? 'quantity' : 'text'
    },
    inputCSVFile(file) {
      const reader = new FileReader()
      reader.readAsText(file)
      reader.onload = e => {
        const text = e.target.result.split(/\r\n|\r|\n/)
        this.labReport.results = []
        this.errorRows = []
        this.labReport.errorResults = []
        for (let i = 1; i < text.length; i++) {
          if (!text[i]) continue
          const data = text[i].split(',')
          let error = ''
          const filterByNameAndSynonyms = ({ name, synonyms }) =>
            _.some([name, ...synonyms], x =>
              _.isEqual(x.toLowerCase(), data[0].toLowerCase())
            )
          let option = this.getBiomarkersList.filter(filterByNameAndSynonyms)
          if (!option.length)
            error = 'Could not process result, please add manually below:'
          else {
            const filterByUnitAndSynonyms = ({ unit, synonyms }) =>
              _.some([unit, ...synonyms], x => _.isEqual(x, data[2]))
            const unit = option[0].units.filter(filterByUnitAndSynonyms)
            if (!unit.length)
              error = 'Could not process result, please add manually below:'
            else option[0].unit = unit[0].unit
          }

          if (error) {
            this.errorRows.push({ text: text[i], error })
            continue
          }
          this.labReport.results.push({
            ...option[0],
            [this.getInputType(option[0])]: data[1]
          })
        }
        for (let i = 0; i < this.errorRows.length; i++) {
          const data = this.errorRows[i].text.split(',')
          let option = this.getBiomarkersList.filter(
            ({ name }) => name === data[0]
          )
          if (option.length) {
            this.labReport.errorResults.push({
              ...option[0],
              [this.getInputType(option[0])]: data[1]
            })
          } else {
            this.labReport.errorResults.push({
              index: i,
              name: '', // data[0],
              isError: true,
              type: '',
              quantity: '' // data[1]
            })
          }
        }
      }
    },
    inputFHIRFile(file) {
      const reader = new FileReader()
      reader.readAsText(file)
      reader.onload = e => {
        this.labReport.results = []
        this.errorRows = []
        this.labReport.errorResults = []
        const fhir = JSON.parse(e.target.result.toString())
        const entries = fhir.entry
        for (let i = 0; i < entries.length; i++) {
          if (!entries[i]) continue
          const resource = entries[i].resource
          const markerName = resource.code.text
          if (!entries[i].resource.valueQuantity) continue
          const unitName = resource.valueQuantity.unit
          const result = resource.valueQuantity.value
          let error = ''
          const filterByNameAndSynonyms = ({ name, synonyms }) =>
            _.some([name, ...synonyms], x =>
              _.isEqual(x.toLowerCase(), markerName.toLowerCase())
            )
          let option = this.getBiomarkersList.filter(filterByNameAndSynonyms)
          if (!option.length)
            error = 'Could not process result, please add manually below:'
          else {
            const filterByUnitAndSynonyms = ({ unit, synonyms }) =>
              _.some([unit, ...synonyms], x => _.isEqual(x, unitName))
            const unit = option[0].units.filter(filterByUnitAndSynonyms)
            if (!unit.length)
              error = 'Could not process result, please add manually below:'
            else option[0].unit = unit[0].unit
          }

          if (error) {
            const tuple = markerName + ',' + result.toString() + ',' + unitName
            this.errorRows.push({ text: tuple, error })
            continue
          }
          this.labReport.results.push({
            ...option[0],
            [this.getInputType(option[0])]: result
          })
        }
        for (let i = 0; i < this.errorRows.length; i++) {
          const data = this.errorRows[i].text.split(',')
          let option = this.getBiomarkersList.filter(
            ({ name }) => name === data[0]
          )
          if (option.length) {
            this.labReport.errorResults.push({
              ...option[0],
              [this.getInputType(option[0])]: data[1]
            })
          } else {
            this.labReport.errorResults.push({
              index: i,
              name: '', // data[0],
              isError: true,
              type: '',
              quantity: '' // data[1]
            })
          }
        }
      }
    }
  }
}
</script>

<template>
  <div>
    <ContentLoader
      v-if="loading"
      :height="150"
      :width="isMobile ? 200 : 400"
      :speed="3"
    >
      <rect x="0" y="0" rx="3" ry="3" width="155" height="12" />
      <rect x="0" y="20" rx="3" ry="3" width="350" height="4" />
      <rect x="0" y="30" rx="3" ry="3" width="380" height="4" />
      <rect x="0" y="40" rx="3" ry="3" width="201" height="4" />
    </ContentLoader>
    <div v-if="!loading" class="container">
      <h3 class="title is-4">Test date</h3>
      <div class="add-lab-report-date">
        <div class="field">
          <div class="control" style="max-width:300px">
            <b-datepicker
              v-model="labReport.date"
              placeholder="Select a date"
              icon="calendar-day"
              icon-pack="fas"
              tabindex="1"
            />
            <p class="help">When was the blood, saliva, etc. taken?</p>
          </div>
        </div>
        <hr class="spacer" />
        <h3 class="title is-4">Lab name</h3>
        <div class="field">
          <div class="control">
            <input
              v-model="labReport.lab"
              placeholder="Laboratory name"
              class="input"
              type="text"
              style="max-width:300px"
              autofocus
              tabindex="2"
            />
          </div>
        </div>
      </div>
      <hr class="spacer" />
      <b-field class="file">
        <b-upload v-model="file" @input="inputCSVFile">
          <a class="button is-primary">
            <b-icon icon="upload"></b-icon>
            <span>Upload CSV file</span>
          </a>
        </b-upload>
      </b-field>
      <b-field class="file">
        <b-upload v-model="file" @input="inputFHIRFile">
          <a class="button is-primary">
            <b-icon icon="upload"></b-icon>
            <span>Upload FHIR file</span>
          </a>
        </b-upload>
      </b-field>
      <h3 class="title is-4 is-spaced">Biomarkers</h3>
      <!-- Pick First Biomarker -->
      <div v-if="!labReport.results.length" class="columns container">
        <div class="column is-3">
          <div class="field">
            <div class="control">
              <div class="is-primary is-fullwidth">
                <b-autocomplete
                  ref="inputFirstBiomarker"
                  v-model="searchedBiomarker"
                  :data="getSearchedBiomarkers"
                  placeholder="e.g. Vitamin B12"
                  icon="magnify"
                  :keep-first="true"
                  :tabindex="2 + labReport.results.length * 3 + 1"
                  field="name"
                  @select="selectBiomarker"
                >
                  <template slot-scope="props">
                    <span
                      :class="[
                        'tag',
                        { 'is-accent': props.option.type == 'BLOOD' },
                        { 'is-warning': props.option.type == 'URINE' },
                        { 'is-info': props.option.type == 'SALIVA' },
                        { 'is-secondary': props.option.type == 'OTHER' }
                      ]"
                      >{{ props.option.type }}</span
                    >
                    &nbsp;
                    {{ props.option.name }}
                  </template>
                  <template slot="empty">No results found</template>
                </b-autocomplete>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        v-for="(item, index) in labReport.results"
        :key="item.id"
        class="add-lab-report-items"
      >
        <LabReportItem
          :item="item"
          :index="index"
          :biomarkers="getSearchedBiomarkers"
          @updateBiomarker="updateBiomarker"
        />
      </div>
      <hr class="spacer" />
      <b-notification
        v-if="labReport.errorResults && labReport.errorResults.length"
        type="is-warning"
        :closable="false"
        role="alert"
      >
        <div v-for="(item, index) in labReport.errorResults" :key="item.id">
          <p>{{ errorRows[index].text }} - {{ errorRows[index].error }}</p>
          <LabReportErrorItem
            :item="item"
            :index="index"
            :biomarkers="getSearchedBiomarkers"
            @updateBiomarker="updateErrorBiomarker"
          />
        </div>
      </b-notification>
      <!-- Add New Biormarker Item -->
      <div
        v-if="labReport.results.length && getBiomarkersList.length"
        class="columns"
      >
        <div class="column is-4">
          <div class="field">
            <div class="control">
              <b-autocomplete
                ref="inputNewBiomarker"
                v-model="searchedBiomarker"
                :data="getSearchedBiomarkers"
                placeholder="Add another biomarker"
                icon="magnify"
                :keep-first="true"
                :tabindex="2 + labReport.results.length * 3 + 1"
                field="name"
                @select="selectBiomarker"
              >
                <template slot-scope="props">
                  <span
                    :class="[
                      'tag',
                      { 'is-accent': props.option.type == 'BLOOD' },
                      { 'is-warning': props.option.type == 'URINE' },
                      { 'is-info': props.option.type == 'SALIVA' },
                      { 'is-secondary': props.option.type == 'OTHER' }
                    ]"
                    >{{ props.option.type }}</span
                  >
                  &nbsp;
                  {{ props.option.name }}
                </template>
                <template slot="empty">No results found</template>
              </b-autocomplete>
            </div>
          </div>
        </div>
        <div class="column is-pulled-right">
          <button
            class="button is-rounded is-secondary is-pulled-right"
            :tabindex="2 + labReport.results.length * 3 + 2"
            @click="submit"
          >
            Save report
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="sass">
.save-button
  margin-top: 5%
</style>
